import React from 'react';
import { Box, Typography } from '@mui/material';
import { ACCOUNT_LIFECYCLE_STATUS, ACCOUNT_LIFECYCLE_STATUS_VERBOSE } from '../../../utils/constants';

/**
 * Draft / Published / Expired / Archived badge for an ACCOUNT — a school or
 * network workspace, and later a class.
 *
 * Not to be confused with ProgramPublishBadge, which shows a *learning
 * program's* own lifecycle. The two look alike deliberately (same pill, same
 * palette) but they are separate states on separate things.
 *
 * A missing status means the account was created before the lifecycle existed
 * and is live — it renders as Published, matching the server-side fallback in
 * services/common/account_lifecycle.js. Note DRAFT is 0 and therefore falsy, so
 * the check is against null/undefined rather than truthiness.
 */
const PALETTE = {
    [ACCOUNT_LIFECYCLE_STATUS.DRAFT]:     { color: '#9E7B00', bg: 'rgba(158, 123, 0, 0.1)' },
    [ACCOUNT_LIFECYCLE_STATUS.PUBLISHED]: { color: '#2e7d32', bg: 'rgba(46, 125, 50, 0.1)' },
    [ACCOUNT_LIFECYCLE_STATUS.EXPIRED]:   { color: '#b26a00', bg: 'rgba(178, 106, 0, 0.12)' },
    [ACCOUNT_LIFECYCLE_STATUS.ARCHIVED]:  { color: '#616161', bg: 'rgba(97, 97, 97, 0.12)' },
};

export const accountLifecycleStatus = (status) => {
    if (status === null || status === undefined) return ACCOUNT_LIFECYCLE_STATUS.PUBLISHED;
    const value = Number(status);
    return PALETTE[value] ? value : ACCOUNT_LIFECYCLE_STATUS.DRAFT;
};

export const isAccountDraft = (status) =>
    accountLifecycleStatus(status) === ACCOUNT_LIFECYCLE_STATUS.DRAFT;

export const isAccountPublished = (status) =>
    accountLifecycleStatus(status) === ACCOUNT_LIFECYCLE_STATUS.PUBLISHED;

const AccountLifecycleBadge = ({ status, style }) => {
    const value = accountLifecycleStatus(status);
    const { color, bg } = PALETTE[value];

    return (
        <Box
            component="span"
            style={style}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                flexShrink: 0,
                height: '20px',
                padding: '0 8px',
                borderRadius: '10px',
                backgroundColor: bg,
                whiteSpace: 'nowrap',
                lineHeight: 1,
            }}
        >
            <Box
                component="span"
                sx={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0,
                }}
            />
            <Typography
                component="span"
                sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                    color,
                    lineHeight: 1,
                }}
            >
                {ACCOUNT_LIFECYCLE_STATUS_VERBOSE[value]}
            </Typography>
        </Box>
    );
};

export default AccountLifecycleBadge;
